import { AI_RUN_EVENT_TYPES, AiRunEventV1 } from './ai-events.js';
import { APP_EVENT_TYPES, AppEventV1 } from './app-events.js';
/**
 * SSE wire format. A frame is `id: <id>` (optional) and one `data: <json>` line, ended by a blank
 * line. The JSON carries the event's `type`, so a client listens to `message` only and an unknown
 * type reaches it (and is counted) instead of being dropped by the browser. A decoder also accepts
 * an `event:` field, which must then equal the JSON type, and ignores comments and `retry:`.
 */
export class SseFrameError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SseFrameError';
    }
}
export function createSseStats() {
    return { unknown: 0, unknownByType: {} };
}
function withId(value, id) {
    return id === undefined ? value : { ...value, id };
}
/** Encode one event as a frame. An invalid event or an id with a line break throws. */
export function encodeSseFrame(event, id) {
    const isAi = typeof event === 'object' && event !== null && 'runId' in event;
    const parsed = isAi ? AiRunEventV1.safeParse(event) : AppEventV1.safeParse(event);
    if (!parsed.success) {
        throw new SseFrameError(`cannot encode an invalid ${isAi ? 'AI run' : 'app'} event`);
    }
    let frame = '';
    if (id !== undefined) {
        const text = String(id);
        if (/[\r\n]/.test(text))
            throw new SseFrameError('an event id cannot contain a line break');
        frame += `id: ${text}\n`;
    }
    return `${frame}data: ${JSON.stringify(event)}\n\n`;
}
/**
 * Decode the JSON payload of one event (what `EventSource` hands a `message` listener).
 * An unknown type, or a known app type at another version, is returned as unknown and counted;
 * a payload that is not a JSON object with a string `type`, or a known type with invalid data,
 * throws {@link SseFrameError}.
 */
export function decodeSseData(data, options = {}) {
    let value;
    try {
        value = JSON.parse(data);
    }
    catch {
        throw new SseFrameError('event data is not JSON');
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new SseFrameError('event data is not a JSON object');
    }
    const record = value;
    const type = record.type;
    if (typeof type !== 'string' || type === '')
        throw new SseFrameError('event has no type');
    if (options.event !== undefined && options.event !== type) {
        throw new SseFrameError(`event field "${options.event}" disagrees with type "${type}"`);
    }
    if ('runId' in record) {
        if (AI_RUN_EVENT_TYPES.includes(type)) {
            const parsed = AiRunEventV1.safeParse(record);
            if (!parsed.success)
                throw new SseFrameError(`malformed AI run event "${type}"`);
            return withId({ kind: 'ai', unknown: false, event: parsed.data }, options.id);
        }
    }
    else if (APP_EVENT_TYPES.includes(type) && record.v === 1) {
        const parsed = AppEventV1.safeParse(record);
        if (!parsed.success)
            throw new SseFrameError(`malformed app event "${type}"`);
        return withId({ kind: 'app', unknown: false, event: parsed.data }, options.id);
    }
    if (options.stats) {
        options.stats.unknown += 1;
        options.stats.unknownByType[type] = (options.stats.unknownByType[type] ?? 0) + 1;
    }
    return withId({ kind: 'unknown', unknown: true, type, data: record.data }, options.id);
}
/**
 * Decode one frame. A frame holding only comments (a keep-alive) decodes to null. A frame with
 * no data line, more than one event, or a malformed payload throws {@link SseFrameError}; an
 * unknown type never throws.
 */
export function decodeSseFrame(text, stats) {
    const lines = text.split(/\r\n|\r|\n/);
    while (lines.length > 0 && lines[lines.length - 1] === '')
        lines.pop();
    const data = [];
    let id;
    let event;
    let sawField = false;
    for (const line of lines) {
        if (line === '')
            throw new SseFrameError('a frame holds exactly one event');
        if (line.startsWith(':'))
            continue;
        const colon = line.indexOf(':');
        const name = colon < 0 ? line : line.slice(0, colon);
        let value = colon < 0 ? '' : line.slice(colon + 1);
        if (value.startsWith(' '))
            value = value.slice(1);
        sawField = true;
        if (name === 'data')
            data.push(value);
        else if (name === 'id')
            id = value;
        else if (name === 'event')
            event = value;
    }
    if (!sawField)
        return null;
    if (data.length === 0)
        throw new SseFrameError('frame has no data');
    return decodeSseData(data.join('\n'), { id, event, stats });
}
//# sourceMappingURL=wire.js.map